<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.pra.hold.release_dialog.title") }}
      </v-card-title>
      <v-card-subtitle class="pra-wrap">
        {{ subtitle }}
      </v-card-subtitle>

      <v-card-text>
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ error }}
        </v-alert>

        <v-textarea
          v-model="reason"
          variant="outlined"
          density="comfortable"
          rows="3"
          auto-grow
          :counter="reasonMax"
          :maxlength="reasonMax"
          :label="`${$t('acceptance.pra.file_change.reason')} *`"
          :disabled="saving"
        />

        <div v-if="note" class="text-body-2 text-grey-darken-1 mt-2">
          {{ note }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.pra.crd.dialog.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="submit"
        >
          {{ $t("acceptance.pra.hold.release_dialog.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {
  RELEASE_REASON_MAX,
  holdTypeKey,
  releaseFormErrors,
  releaseNote,
} from "@/components/Acceptance/praHoldForm.mjs";

// "Rilascia sospensione" (Stage 2 slice 8): a required reason (at most 500 characters). The case
// keeps its state; the note says which step can be recorded again. The page sends it through the
// case session; a save error keeps the dialog open with the input and the server message.
export default {
  name: "AcceptancePraReleaseDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    // Save failure from the page, shown inside the dialog; the input is kept.
    error: { type: String, default: "" },
    // The case's praCase (its state and activeHold), or null
    praCase: { type: Object, default: null },
  },
  emits: ["update:modelValue", "submit"],
  data: () => ({
    reason: "",
  }),
  computed: {
    reasonMax() {
      return RELEASE_REASON_MAX;
    },
    valid() {
      return (
        Object.keys(releaseFormErrors({ reason: this.reason })).length === 0
      );
    },
    // "Fermo fiscale · sospesa da Anna Neri il 19/10/2026"
    subtitle() {
      const hold = this.praCase && this.praCase.activeHold;
      if (!hold) return "";
      const key = holdTypeKey(hold.type);
      return this.$t("acceptance.pra.hold.release_dialog.subtitle", {
        type: key ? this.$t(key) : hold.type,
        name: (hold.placedBy && hold.placedBy.name) || "—",
        date: hold.placedAt ? moment(hold.placedAt).format("DD/MM/YYYY") : "—",
      });
    },
    // "La pratica torna a «CRD registrato» e si può registrare la richiesta di radiazione."
    note() {
      const keys = releaseNote(this.praCase);
      if (!keys) return "";
      return this.$t("acceptance.pra.hold.release_dialog.note", {
        state: this.$t(keys.stateKey),
        step: this.$t(keys.stepKey),
      });
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reason = "";
    },
  },
  methods: {
    submit() {
      if (!this.valid || this.saving) return;
      this.$emit("submit", { reason: this.reason.trim() });
    },
  },
};
</script>

<style scoped>
.pra-wrap {
  white-space: normal;
}
</style>
